import { SotModalProps } from "../types";
import ModalComponent from '@/common/Modal';
import { Box, Button, FormControl, FormLabel, Image, Text, Spinner } from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { useDropzone } from "react-dropzone";
import { getIsProcessCompleted, getUserEmail } from "@/redux/app-selectors";
import { useDispatch, useSelector } from 'react-redux';
import { setIsProcessCompleted, setRefreshStatus } from "@/redux/app-slice";
import { RootState } from "@/redux/store";
import { useParams } from "react-router-dom";
import { callToGetDealerDetail, callToGetOCRStepFunctionDetailsV2, callToManualReviewRequest, certificateWorkflowInitialState, setCallOcrStepFunctionApi, setIsManualReviewRequested, setIsOCRLoading, setOcrStepFunctionStatusV2 } from "../ducks/slice";
import { fileUploadDescription, fileUploadMessage, sotUnderReviewMessage } from "@/utils/constants";
import { getIsManualReviewRequested, getIsOCRLoading, getOcrStepFunctionStatus } from "../ducks/selectors";
import Steppers from "./Steppers";
import mime from 'mime-types';
import path from 'path';
import { v4 as uuidv4 } from "uuid";
import DocumentValidationError from "./document-validation-error";
import DocumentValidationInstruction from "./document-validation-instruction";
import { uploadData } from "aws-amplify/storage";

interface SotFormProps {
  files: any;
  onFileChange: (file: any) => void;
  isFileUploaded: boolean;
  setErrorStepIndex: (index: number) => void;
  setSuccessStepIndex: (index: number) => void;
  setActiveStepIndex: (index: number) => void;
}

export function SotForm({ files, onFileChange, isFileUploaded, setErrorStepIndex, setActiveStepIndex, setSuccessStepIndex }: SotFormProps) {
  // Accept file that is of type image/pdf
  const { acceptedFiles, fileRejections, getRootProps, getInputProps, isDragActive, open: openFileDialog } = useDropzone({
    accept: {
      "image/jpeg": [],
      "image/png": [],
      "image/jpg": [],
      "application/pdf": [],
    },
    noClick: true,
    noKeyboard: true,
    onDrop: (acceptedFiles: any) => {
      if (acceptedFiles.length) {
        onFileChange(acceptedFiles.map((file: any) =>
          Object.assign(file, {
            preview: URL.createObjectURL(file),
          })
        ))
        setErrorStepIndex(0);
        setSuccessStepIndex(1);
        setActiveStepIndex(2);
      }
    },
    multiple: false,
  })

  // Error for rejecting the file and compute the rejected file size
  const fileRejectionItems = fileRejections.map(({ file, errors }: any) => (
    <li key={file.path}>
      {file.path} - {file.size} bytes
      <ul>
        {errors.map((e: any) => (
          <li key={e.code}>{e.message}</li>
        ))}
      </ul>
    </li>
  ));

  // Preview the file uploaded
  const thumbs = files.map((file: any) => {
    return (
      <div className="clear-both" key={file.name}>
        {file.type === "application/pdf" ? (
          <iframe
            src={file.preview}
            width="100%"
            className="docs-iframe"
            id="file-preview-iframe"
          /* @ts-ignore */
          ></iframe>
        )
          :
          <Image
            src={file.preview}
            alt="Preview"
            style={{ width: '100%', height: 'auto' }}
            className="image-preview"
          />
        }
      </div>
    );
  });

  const getYearBasedOnCutoff = () => {
    const today = new Date();
    const currentYear = today.getFullYear();
    const currentMonth = today.getMonth();
    const currentDate = today.getDate();
    
    // Check if today is on or before June 30th
    if (currentMonth < 5 || (currentMonth === 5 && currentDate <= 30)) {
        return currentYear;
    } else {
        return currentYear + 1;
    }
  };
  return (
    <Box className="sot_form_wrapper" sx={{ display: 'flex', flexDirection: 'column', flexGrow: '1', overflow: 'auto' }}>
      <Box className="sot_form_label">Upload your {getYearBasedOnCutoff()} SOT Tax Stamp</Box>
      <Box className="upload_file_container" sx={{ overflow: 'auto' }}>
        {/* On click upload button, show progress bar for file upload status  */}
        {isFileUploaded ?
          <Box className="upload_file_progress_wrapper">
            <Spinner
              thickness={'4px'}
              speed="0.65s"
              emptyColor={'gray.200'}
              color="#0C1E46"
              size={'xl'}
            />
            <Text>Please wait while the file is uploading</Text>
          </Box>
          :
          files && files.length > 0 && files[0].size / (1024 * 1024) <= 8 ?
            (
              <>
                <Box className="file-preview-modal">
                  <Box>{thumbs}</Box>
                </Box>
              </>
            ) :
            <Box className="upload_file_wrapper" onClick={openFileDialog}>
              <Image className="upload_file_icon" src="/images/ui/upload.svg" alt="upload-icon" />
              <Box className="upload_file_description_text" {...getRootProps()}>
                <FormControl>
                  <FormLabel style={{ textAlign: 'center', width: '60%', margin: 'auto' }} >
                    Click here to upload a file (File format- jpeg/png/pdf)</FormLabel>
                  <input type='file' {...getInputProps()} style={{ display: 'none' }} />
                  {fileRejectionItems.length ? (
                    <Text style={{ color: 'red', fontWeight: 'bold' }}>
                      Please select file from one of the above types.
                    </Text>
                  ) : (<></>)}
                  {files && files.length > 0 && files[0].size / (1024 * 1024) > 8 && (<Text style={{ color: 'red', fontWeight: '500', marginTop: '4px' }} >*Please upload file with size less than 8MB*</Text>)
                  }
                </FormControl>
              </Box>
            </Box>
        }

      </Box>
    </Box>
  )
}

export default function SotModal({
  data,
  isOpen,
  onClose,
}: SotModalProps) {
  const dispatch = useDispatch()
  const params: any = useParams();
  const [files, setFiles] = useState<any>([]);
  const [isFileUploaded, setIsFileUploaded] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const processCompleted = getIsProcessCompleted();

  const userEmail = getUserEmail()

  const [activeStepIndex, setActiveStepIndex] = useState(1);
  const [successStepIndex, setSuccessStepIndex] = useState(0);
  const [errorStepIndex, setErrorStepIndex] = useState(0);
  const [isDocumentValidationError, setIsDocumentValidationError] = useState(false)
  const [isReviewPending, setIsReviewPending] = useState(false)
  const ocrStepFunctionStatus: any = getOcrStepFunctionStatus();
  const [isFileUploadedSuccess, setIsFileUploadedSuccess] = useState(false)
  const isOcrLoading = getIsOCRLoading();
  const isManualReviewRequested = getIsManualReviewRequested();
  const [documentId, setDocumentId] = useState('');

  const refreshStatus = useSelector(
    (state: RootState) => state.app.refreshStatus
  )

  useEffect(() => {
    setDocumentId('')
    dispatch(setIsProcessCompleted(false))
    setIsFileUploaded(false)
    setFiles([])
    setIsLoading(false)
    dispatch(setOcrStepFunctionStatusV2(certificateWorkflowInitialState));
    setIsFileUploadedSuccess(false)
    setActiveStepIndex(1)
    setSuccessStepIndex(0)
    setErrorStepIndex(0)
    setIsReviewPending(false);
    setIsDocumentValidationError(false)
    dispatch(setIsManualReviewRequested(false));
  }, [isOpen])

  useEffect(() => {
    if (isManualReviewRequested) {
      setErrorStepIndex(0);
      setIsReviewPending(true)
      setIsLoading(false)
      setIsDocumentValidationError(false)
    }
  }, [isManualReviewRequested]);

  const onCloseHandler = () => {
    onClose()
    setDocumentId('')
    dispatch(setIsProcessCompleted(false))
    setIsFileUploaded(false)
    setFiles([])
    setIsLoading(false)
    setIsFileUploadedSuccess(false)
    setActiveStepIndex(1)
    setSuccessStepIndex(0)
    setErrorStepIndex(0)
    setIsDocumentValidationError(false)

    setTimeout(() => {
      setIsReviewPending(false);
      dispatch(setIsManualReviewRequested(false));
      dispatch(setOcrStepFunctionStatusV2(certificateWorkflowInitialState));
  }, 200);
    if (refreshStatus) {
      dispatch(callToGetDealerDetail(params?.dealerId));
      dispatch(setRefreshStatus(false));
    }
  }


  useEffect(() => {
    if (ocrStepFunctionStatus?.status == null) {
      setIsLoading(false)
      setIsFileUploadedSuccess(false)
      setActiveStepIndex(1)
      setSuccessStepIndex(0)
      setErrorStepIndex(0)
      setIsReviewPending(false);
      setIsDocumentValidationError(false)
      setDocumentId('')
      return
    }
    if (ocrStepFunctionStatus?.status !== 0 && ocrStepFunctionStatus?.status !== null) {
      dispatch(setCallOcrStepFunctionApi(false));
      if (ocrStepFunctionStatus.status === 13 ||
        ocrStepFunctionStatus.status === 54 ||
        ocrStepFunctionStatus.status === 54 ||
        ocrStepFunctionStatus.status === 14 ||
        ocrStepFunctionStatus.status === 33 ||
        ocrStepFunctionStatus.status === 16 ||
        ocrStepFunctionStatus.status === 15 ||
        ocrStepFunctionStatus.status === 51 ||
        ocrStepFunctionStatus.status === 52 ||
        ocrStepFunctionStatus.status === 18 
      ) {
        setIsDocumentValidationError(true);
        setSuccessStepIndex(2);
        setErrorStepIndex(3);
      } else {
        setSuccessStepIndex(2);
        setActiveStepIndex(3)
        setIsReviewPending(true)
        setIsDocumentValidationError(false);
        setIsLoading(false)
      }
    } else if (ocrStepFunctionStatus) {
      if (ocrStepFunctionStatus.status === 0) {
        dispatch(setCallOcrStepFunctionApi(false))
        setSuccessStepIndex(3)
        setIsLoading(false)
        setIsFileUploaded(false)
        setIsFileUploadedSuccess(true)
        setDocumentId('')
      }
    }
  }, [ocrStepFunctionStatus]);

  // create an S3 bucket, upload file and track uploading progress
  const uploadSOTInS3 = async (files: any) => {
    try {
      if (files) {
        setSuccessStepIndex(2);
        setActiveStepIndex(3);
        const contentType = mime.lookup(files[0].path.split('.').pop() as string) ||
          'application/octet-stream';
        const document_id: any = uuidv4();
        // const res = await Storage.put(`${document_id}/${files[0].path}`, files[0], {
        //   contentType: contentType,
        //   metadata: {
        //     source: "ffl360",
        //     documentType: "sot",
        //     document_type: 'sot',
        //     email: userEmail,
        //     document_id: document_id,
        //     license_key: data?.licenses?.find((record) => record?.licenseKey === data.primaryInboundDetails.licenseKey)?.fullLicenseKey || ""
        //   },
        //   customPrefix: { public: `${data.primaryInboundDetails.licenseKey}/` },
        // });
        const res = await uploadData({
          path: `${data.primaryInboundDetails.licenseKey}/${document_id}/${files[0].name}`,
          data: files[0],
          options: {
            contentType: contentType,
            bucket: "licenseReader",
            metadata: {
              source: "ffl360",
              documentType: "sot",
              document_type: 'sot',
              email: userEmail,
              document_id: document_id,
              license_key: data?.licenses?.find((record) => record?.licenseKey === data.primaryInboundDetails.licenseKey)?.fullLicenseKey || ""
            }
          }
        }).result;
        const fileName = path.basename(files[0].path);
        if (res.path) {
          dispatch(setIsOCRLoading(true));
          dispatch(setCallOcrStepFunctionApi(true));
          setDocumentId(document_id)
          dispatch(callToGetOCRStepFunctionDetailsV2({ clientDocumentId: document_id }));
        }
        dispatch(setIsProcessCompleted(!!res.path))
      }
    } catch (error) {
      console.log({ error });
      dispatch(setRefreshStatus(true))
      setIsFileUploadedSuccess(false)
      setDocumentId('')
    }
  }

  useEffect(() => {
    if (processCompleted) {
      setIsLoading(false)
    }
  }, [processCompleted])

  const onUploadNewFile = () => {
    dispatch(setIsProcessCompleted(false))
    dispatch(setOcrStepFunctionStatusV2(certificateWorkflowInitialState));
    setIsFileUploaded(false)
    setFiles([])
    setIsLoading(false)
    setIsFileUploadedSuccess(false)
    setActiveStepIndex(1)
    setSuccessStepIndex(0)
    setErrorStepIndex(0)
    setIsReviewPending(false);
    setIsDocumentValidationError(false)
    setDocumentId('')
    dispatch(setIsManualReviewRequested(false));
  }
   const onMannualRequest = () => {
      dispatch(setIsManualReviewRequested(false));
      dispatch(callToManualReviewRequest({ clientDocumentId: documentId }));
    }

  return (
    <ModalComponent
      title="SOT"
      isOpen={isOpen}
      onClose={onCloseHandler}
      variant={2}
      className="upload-license-modal"
      // maxWidth={"534"}
      footer={
        isOcrLoading || isLoading || isDocumentValidationError ? <></> : (
          (!data?.sot?.isPending  && (!isFileUploadedSuccess) && !isReviewPending )?
            <Button
              colorScheme="blue"
              background="#0C1E46"
              padding="8px 20px"
              onClick={() => {
                uploadSOTInS3(files)
                setIsFileUploaded(true)
                setIsLoading(true)
              }}
              isDisabled={isLoading || isFileUploadedSuccess || files.length === 0 || (files[0]?.size / (1024 * 1024) > 8)}
            >
              Upload
            </Button>
            : <Button
              colorScheme="blue"
              background="#0C1E46"
              padding="8px 20px"
              onClick={onCloseHandler}
              isDisabled={isLoading}
            >
              Done
            </Button>
        )
      }
    >
      {data?.sot?.isPending ?
        <Box className='file_upload_successful'>
          <Image src="/images/ui/pending.svg" alt="success-icon" />
          <Box>
            <Text className="file_upload_success_message">{sotUnderReviewMessage}</Text>
          </Box>
        </Box>
        :
        <Box className='Upload-doc-wrapper'>
          <Box className="step_header">
            <Box className="stepperMain">
              <Steppers
                successSteps={successStepIndex}
                activeIndex={activeStepIndex}
                errorIndex={errorStepIndex}
              />
            </Box>
          </Box>
          {(isFileUploadedSuccess ?
            <Box className='file_upload_successful'>
              <Image src="/images/ui/success-icon.svg" alt="success-icon" />
              <Text className='file_upload_success_message'>{fileUploadMessage}</Text>
              <Box className='file_upload_success_description'>
                <Text>{fileUploadDescription}</Text>
                <Text>Please check your profile after 15 minutes, if SOT information is not updated kindly contact at support@masterffl.com.</Text>
              </Box>
            </Box>
            :
            <>
              {isDocumentValidationError ? (
                <DocumentValidationError documentTypeLabel="SOT" OnUploadNewFile={onUploadNewFile}  onMannualRequest={() => onMannualRequest()} />
              ) : (
                <>
                  {isFileUploaded ? (
                    <DocumentValidationInstruction isReviewPending={isReviewPending}  isManualReviewRequested={isManualReviewRequested} />
                  ) : (
                    <SotForm
                      files={files}
                      onFileChange={setFiles}
                      isFileUploaded={isFileUploaded}
                      setErrorStepIndex={setErrorStepIndex}
                      setSuccessStepIndex={setSuccessStepIndex}
                      setActiveStepIndex={setActiveStepIndex}
                    />
                  )}
                </>
              )}
            </>
          )}
        </Box>
      }
    </ModalComponent>
  )

}